<template>
  <div class="game-category">
    <atomic-page-title @on-click="router.back()">
      {{ getContent(providersContent?.currentLocaleData, providersContent?.defaultLocaleData, 'title') }}
    </atomic-page-title>

    <div class="game-category-filters">
      <form-input-search
        v-model:value="searchValue"
        class="game-category-filters__search"
        :placeholder="getContent(layoutData, defaultLocaleLayoutData, 'header.search.placeholder')"
        :hasClearIcon="!!searchValue"
        @input="searchInput"
      />

      <bottom-sheet-games-sorting
        v-if="isMobile"
        :sortBy="sortBy"
        :sortOrder="sortOrder"
        :sortOptions="sortOptions"
        heightBottomSheet="50"
      />

      <sorting-games-sorting v-else :sortBy="sortBy" :sortOrder="sortOrder" :sortOptions="sortOptions" />
    </div>

    <list-skeleton-preloader v-if="loadingProviders" customClass="skeleton-list-preloading--provider" />

    <list-providers v-if="getFilteredProviders.length && !loadingProviders" :providersList="getFilteredProviders" />

    <atomic-custom-empty
      v-if="!getFilteredProviders.length && !loadingProviders"
      :title="getContent(layoutData, defaultLocaleLayoutData, 'empty.nothingFound.title')"
      :description="getContent(layoutData, defaultLocaleLayoutData, 'empty.nothingFound.description')"
      :image="getContent(layoutData, defaultLocaleLayoutData, 'empty.nothingFound.img')"
    />
  </div>
</template>

<script setup lang="ts">
  import debounce from 'lodash/debounce';

  import { isModalQueryChanged } from '@skeleton/utils/modalQueryUtils';

  import { SortByFields } from '@skeleton/consts/sort';
  import type { IProvidersPage } from '~/types';
  import type { IProvidersRequest } from '@skeleton/core/types';

  const globalStore = useGlobalStore();
  const { layoutData, defaultLocaleLayoutData } = storeToRefs(globalStore);
  const deviceStore = useDeviceStore();
  const { isMobile } = storeToRefs(deviceStore);
  const { getProviders } = useGameProvidersStore();
  const { getFilteredProviders } = storeToRefs(useGameProvidersStore());
  const { getContent } = useProjectMethods();

  const { getContentData } = useContentLogic<IProvidersPage>({
    contentKey: 'providersPageContent',
    contentRoute: ['pages', 'providers'],
    isPage: true,
  });

  const { data: providersContent } = await useLazyAsyncData(getContentData);
  const { sortOptions, parseSortQuery } = await useSortingOptions();

  const route = useRoute();
  const router = useRouter();

  const loadingProviders = ref(true);

  const searchValue = ref('');

  const sortBy = ref<string | undefined>();
  const sortOrder = ref<string | undefined>();

  const defaultRequestParams = computed<IProvidersRequest>(() => {
    return {
      name: searchValue.value || undefined,
      sortBy: sortBy.value === SortByFields.DEFAULT ? undefined : sortBy.value,
      sortOrder: sortBy.value === SortByFields.DEFAULT ? undefined : sortOrder.value,
    };
  });

  const updateQueryWithReplace = () => {
    router.replace({
      query: {
        ...route.query,
        sortBy: sortBy.value,
        sortOrder: sortOrder.value,
      },
    });
  };

  const setActiveSort = () => {
    const parsedQuery = parseSortQuery(route.query);
    sortOrder.value = parsedQuery.sortOrder;
    sortBy.value = parsedQuery.sortBy;
  };

  const searchInput = debounce(async () => {
    if (searchValue.value.length > 1 || !searchValue.value) {
      loadingProviders.value = true;
      await loadProviders();
    }
  }, 500);

  const loadProviders = async () => {
    await getProviders(defaultRequestParams.value, true);
    loadingProviders.value = false;
  };

  watch(
    () => route.query,
    (newQuery, oldQuery) => {
      if (!Object.keys(oldQuery).length) return;
      if (isModalQueryChanged(newQuery, oldQuery)) return;

      loadingProviders.value = true;
      setActiveSort();
      loadProviders();
    }
  );

  onMounted(() => {
    setActiveSort();
    loadProviders();
    updateQueryWithReplace();
  });
</script>

<style src="~/assets/styles/pages/games/game-category.scss" lang="scss" />
